import { useState, useEffect } from 'react';
import { TechniqueFilters, TechniqueDifficulty, TechniqueType, TechniquePosition } from '@/types/technique';
import { useSearchParams } from 'react-router-dom';
import { FilterOption } from '@/components/filters/FilterCombobox';

export const useTechniqueFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const [selectedPosition, setSelectedPosition] = useState<string>(searchParams.get('position') || 'all');
  const [selectedLevel, setSelectedLevel] = useState<string>(searchParams.get('difficulty') || 'all');
  const [selectedType, setSelectedType] = useState<string>(searchParams.get('techniqueType') || 'all');
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('category') || 'all');
  const [excludeUserCreated, setExcludeUserCreated] = useState<boolean>(
    searchParams.get('excludeUserCreated') === 'true'
  );

  // Filter options for dropdowns
  const levelOptions: FilterOption[] = [
    { value: 'all', label: 'All Levels' },
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' }
  ];

  const typeOptions: FilterOption[] = [
    { value: 'all', label: 'All Types' },
    { value: 'choke', label: 'Choke' },
    { value: 'joint_lock', label: 'Joint Lock' },
    { value: 'sweep', label: 'Sweep' },
    { value: 'takedown', label: 'Takedown' },
    { value: 'control', label: 'Control' },
    { value: 'escape', label: 'Escape' },
    { value: 'defensive', label: 'Defensive' },
    { value: 'guard_passing', label: 'Guard Passing' },
    { value: 'other', label: 'Other' }
  ];

  // Construct filter object for API calls
  const filters: TechniqueFilters = {
    search: searchQuery || undefined,
    position: selectedPosition !== 'all' ? selectedPosition as TechniquePosition : undefined,
    difficulty: selectedLevel !== 'all' ? selectedLevel as TechniqueDifficulty : undefined,
    technique_type: selectedType !== 'all' ? selectedType as TechniqueType : undefined,
    category: selectedCategory !== 'all' ? selectedCategory : undefined,
    exclude_user_created: excludeUserCreated || undefined
  };

  // Update URL search params when filters change (only if actually changed)
  useEffect(() => {
    const newSearchParams = new URLSearchParams();

    if (searchQuery) newSearchParams.set('search', searchQuery);
    if (selectedPosition !== 'all') newSearchParams.set('position', selectedPosition);
    if (selectedLevel !== 'all') newSearchParams.set('difficulty', selectedLevel);
    if (selectedType !== 'all') newSearchParams.set('techniqueType', selectedType);
    if (selectedCategory !== 'all') newSearchParams.set('category', selectedCategory);
    if (excludeUserCreated) newSearchParams.set('excludeUserCreated', 'true');

    const current = searchParams.toString();
    const next = newSearchParams.toString();

    if (current !== next) {
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [
    searchQuery,
    selectedPosition,
    selectedLevel,
    selectedType,
    selectedCategory,
    excludeUserCreated,
    searchParams,
    setSearchParams
  ]);

  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedPosition('all');
    setSelectedLevel('all');
    setSelectedType('all');
    setSelectedCategory('all');
    setExcludeUserCreated(false);
  };

  return {
    searchQuery,
    setSearchQuery,
    selectedPosition,
    setSelectedPosition,
    selectedLevel,
    setSelectedLevel,
    selectedType,
    setSelectedType,
    selectedCategory,
    setSelectedCategory,
    excludeUserCreated,
    setExcludeUserCreated,
    filters,
    levelOptions,
    typeOptions,
    clearAllFilters
  };
};
